import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { DepartmentReportQueryParams, DepartmentReportListItem } from "../types/departmentReport";
import { fetchDepartmentReports } from "../api/departmentReportApi";

export type DepartmentReportPagination = {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
    from: number;
    to: number;
};

const defaultPagination: DepartmentReportPagination = {
    current_page: 1,
    last_page: 1,
    per_page: 20,
    total: 0,
    from: 0,
    to: 0,
};

function buildListIdentity(
    scope: string,
    scopeId: number,
    reportStatusId: number | undefined,
    leadershipType: string | undefined
): string {
    return `${scope}:${scopeId}:${reportStatusId ?? "all"}:${leadershipType ?? "none"}`;
}

export function useDepartmentReports(
    scope: 'CHURCH' | 'DISTRICT' | 'NATION',
    scopeId: number,
    params: DepartmentReportQueryParams
) {
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState<DepartmentReportListItem[]>([]);
    const [pagination, setPagination] = useState<DepartmentReportPagination>(defaultPagination);
    const requestParams = useMemo(
        () => ({
            reportStatusId: params.reportStatusId,
            departmentId: params.departmentId,
            reportTagId: params.reportTagId,
            page: params.page,
            perPage: params.perPage,
            freetext: params.freetext,
            leadershipType: params.leadershipType,
        }),
        [
            params.reportStatusId,
            params.departmentId,
            params.reportTagId,
            params.page,
            params.perPage,
            params.freetext,
            params.leadershipType,
        ]
    );
    const listIdentityRef = useRef(
        buildListIdentity(scope, scopeId, requestParams.reportStatusId, requestParams.leadershipType)
    );
    const requestIdRef = useRef(0);

    const enabled = scopeId > 0;

    const applySuccessPayload = useCallback((payload: any, items: DepartmentReportListItem[]) => {
        setData(items);

        if (payload && !Array.isArray(payload) && typeof payload.current_page === 'number') {
            const perPage = payload.per_page ?? requestParams.perPage ?? 20;
            const total = payload.total ?? 0;
            const currentPage = payload.current_page ?? 1;
            const lastPage = payload.last_page ?? 1;
            setPagination({
                current_page: currentPage,
                last_page: lastPage,
                per_page: perPage,
                total,
                from: total === 0 ? 0 : (currentPage - 1) * perPage + 1,
                to: total === 0 ? 0 : Math.min(currentPage * perPage, total),
            });
        } else {
            setPagination({
                ...defaultPagination,
                total: items.length,
                from: items.length > 0 ? 1 : 0,
                to: items.length,
            });
        }
    }, [requestParams.perPage]);

    const load = useCallback(async () => {
        const listIdentity = buildListIdentity(
            scope,
            scopeId,
            requestParams.reportStatusId,
            requestParams.leadershipType
        );
        const identityChanged = listIdentityRef.current !== listIdentity;
        listIdentityRef.current = listIdentity;

        const requestId = ++requestIdRef.current;
        setLoading(true);

        if (identityChanged) {
            setData([]);
            setPagination(defaultPagination);
        }

        if (!enabled) {
            if (requestId === requestIdRef.current) {
                setLoading(false);
                setData([]);
                setPagination(defaultPagination);
            }
            return;
        }

        const commit = (fn: () => void) => {
            if (requestId === requestIdRef.current) {
                fn();
            }
        };

        try {
            const response = await fetchDepartmentReports(scope, scopeId, requestParams);

            commit(() => {
                if (response.isSuccess()) {
                    const payload = response.getData() as any;
                    const items = Array.isArray(payload) ? payload : payload?.data ?? [];
                    applySuccessPayload(payload, items);
                } else {
                    setData([]);
                    setPagination(defaultPagination);
                }
            });
        } catch (err) {
            console.error("Failed to fetch department reports:", err);
            commit(() => {
                setData([]);
                setPagination(defaultPagination);
            });
        } finally {
            commit(() => {
                setLoading(false);
            });
        }
    }, [applySuccessPayload, enabled, requestParams, scope, scopeId]);

    useEffect(() => {
        void load();

        return () => {
            requestIdRef.current += 1;
        };
    }, [load]);

    return { data, loading, reload: load, pagination };
}
